import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import LanguageMultiSelect from '@/Domain/components/Settings/ai/LanguageMultiSelect';
import { Card } from '@/Domain/components/Shared/Card/Card';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { PrivilegesEnum } from '@/Domain/enum/Privileges.enum';
import { cn } from '@/Domain/utils/cn';
import { AiModelData } from '@/UI/Identity/AiModel/aiModel.constants';
import { AISettings, MAIN_PROMPT_LIMIT_FALLBACK } from '@/UI/Identity/Identity.constants';
import { ControlledSelect } from '@/UI/Identity/components/ControlledSelect';
import { IdentityFieldRow } from '@/UI/Identity/components/IdentityFieldRow';
import { IdentityModelTab } from '@/UI/Identity/components/IdentityModelTab';
import { useIdentityKnowledgeBase } from '@/UI/Identity/components/useIdentityKnowledgeBase';
import { useIdentitySettings } from '@/UI/Identity/components/useIdentitySettings';
import { GuidelinesSheet } from '@/UI/Identity/sheets/GuidelinesSheet';
import { MainPromptSheet } from '@/UI/Identity/sheets/MainPromptSheet';

interface IdentityContentProps {
  settings: AISettings;
  knowledgeBase: KnowledgeBase;
  model?: AiModelData;
}

export const IdentityContent = ({ settings, knowledgeBase, model }: IdentityContentProps) => {
  const { t } = useTranslation(['identity', 'settings', 'knowledge_base', 'common', 'validation']);
  const queryClient = useQueryClient();
  const { isActivated, getConfig, isFetched } = useSubscription();
  // Bootstrap sets ['account'] once and it never changes after, so there's no `account` query to read reactively.
  const account = queryClient.getQueryData<AccountDto>(['account']);

  const [openSheet, setOpenSheet] = useState<null | 'prompt' | 'guidelines'>(null);
  const editLabel = t('common:edit', { defaultValue: 'Edit' });

  const kb = useIdentityKnowledgeBase(knowledgeBase);
  const ai = useIdentitySettings(settings);

  const aiGuidelinesLocked = isFetched && !isActivated('ai_guidelines');
  const hasGuidelines = account?.privileges.includes(PrivilegesEnum.AI_GUIDELINES) ?? false;

  const languageLimit = getConfig('languages') as number | undefined;
  const mainPromptLimit =
    (getConfig('main_prompt_limit') as number | undefined) ?? MAIN_PROMPT_LIMIT_FALLBACK;

  return (
    <Tabs defaultValue="persona">
      <TabsList className="mb-6">
        <TabsTrigger value="persona" data-testid="identity-tab-persona">
          {t('identity:tabs.persona')}
        </TabsTrigger>
        <TabsTrigger value="model" data-testid="identity-tab-model">
          {t('identity:tabs.model')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="persona">
        <Card className="px-6">
          {/* Name */}
          <IdentityFieldRow
            id="one_word_description"
            label={t('knowledge_base:form.name.label')}
            description={t('knowledge_base:form.name.help')}
          >
            <Controller
              control={kb.form.control}
              name="one_word_description"
              rules={{ required: t('required', { ns: 'validation' }), maxLength: 40 }}
              render={({ field }) => {
                const empty = !(field.value ?? '').trim();
                return (
                  <div className="relative">
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      id="one_word_description"
                      size="sm"
                      maxLength={40}
                      error={empty}
                      placeholder={t('knowledge_base:form.name.placeholder')}
                    />
                    {empty && (
                      <FieldError className="absolute left-0 top-full mt-1">
                        {t('required', { ns: 'validation' })}
                      </FieldError>
                    )}
                  </div>
                );
              }}
            />
          </IdentityFieldRow>

          {/* Main Prompt -> sheet */}
          <IdentityFieldRow
            label={t('knowledge_base:form.description.label')}
            description={t('knowledge_base:form.description.help')}
          >
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpenSheet('prompt')}
                data-testid="identity-open-main-prompt"
                className={cn(
                  'block w-full cursor-pointer rounded-xl border bg-card px-3 py-3 text-left transition-colors hover:bg-accent',
                  kb.mainPromptEmpty ? 'border-destructive' : 'border-border',
                )}
              >
                <div className="flex h-[60px] items-start justify-between gap-2">
                  <p className="line-clamp-3 min-w-0 flex-1 text-sm text-muted-foreground">
                    {kb.promptValue}
                  </p>
                  <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                    <Pencil className="size-3.5" />
                    {editLabel}
                  </span>
                </div>
              </button>
              {kb.mainPromptEmpty && (
                <FieldError className="absolute left-0 top-full mt-1">
                  {t('required', { ns: 'validation' })}
                </FieldError>
              )}
            </div>
          </IdentityFieldRow>

          {/* Guidelines -> sheet (only when the account has the privilege) */}
          {hasGuidelines && (
            <IdentityFieldRow
              label={t('knowledge_base:form.commands.label')}
              description={t('knowledge_base:form.commands.help')}
            >
              <button
                type="button"
                onClick={() => setOpenSheet('guidelines')}
                data-testid="identity-open-guidelines"
                className="block w-full cursor-pointer rounded-xl border border-border bg-card px-3 py-3 text-left transition-colors hover:bg-accent"
              >
                <div className="flex h-[60px] items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {kb.commands.slice(0, 2).map((cmd, i) => (
                      <p key={i} className="line-clamp-1 text-sm text-muted-foreground">
                        - {cmd}
                      </p>
                    ))}
                    {kb.commands.length > 2 && (
                      <p className="text-sm text-foreground-subtle">+{kb.commands.length - 2}</p>
                    )}
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                    <Pencil className="size-3.5" />
                    {editLabel}
                  </span>
                </div>
              </button>
            </IdentityFieldRow>
          )}

          {/* Personality */}
          <IdentityFieldRow
            label={t('settings:personality.label')}
            description={t('settings:personality.description')}
          >
            <ControlledSelect
              control={ai.form.control}
              name="personality"
              numeric
              options={ai.personalityOptions}
              data-testid="identity-select-personality"
            />
          </IdentityFieldRow>

          {/* Profession */}
          <IdentityFieldRow
            label={t('settings:profession.label')}
            description={t('settings:profession.description')}
          >
            <ControlledSelect
              control={ai.form.control}
              name="profession"
              options={ai.professionOptions}
              data-testid="identity-select-profession"
            />
          </IdentityFieldRow>

          {/* Creativity */}
          <IdentityFieldRow
            label={t('settings:creativity.label')}
            description={t('settings:creativity.description')}
          >
            <ControlledSelect
              control={ai.form.control}
              name="creativity_level"
              numeric
              options={ai.creativityOptions}
              disabled={ai.aiPreferencesLocked}
              data-testid="identity-select-creativity"
            />
          </IdentityFieldRow>

          {/* Reply length */}
          <IdentityFieldRow
            label={t('settings:reply.label')}
            description={t('settings:reply.description')}
          >
            <ControlledSelect
              control={ai.form.control}
              name="reply_length"
              numeric
              options={ai.replyOptions}
              disabled={ai.aiPreferencesLocked}
              data-testid="identity-select-reply"
            />
          </IdentityFieldRow>

          {/* Links priority */}
          <IdentityFieldRow
            label={t('settings:links_priority.label')}
            description={t('settings:links_priority.description')}
          >
            <ControlledSelect
              control={ai.form.control}
              name="url_priority"
              numeric
              options={ai.linksOptions}
              disabled={ai.aiPreferencesLocked}
              data-testid="identity-select-links"
            />
          </IdentityFieldRow>

          {/* Languages (multi-select) */}
          <IdentityFieldRow
            align="start"
            label={t('settings:languages.title')}
            description={t('settings:languages.description')}
          >
            <Controller
              control={ai.form.control}
              name="languages"
              render={({ field, fieldState: { error } }) => (
                <>
                  <LanguageMultiSelect
                    id="languages"
                    value={field.value}
                    onChange={field.onChange}
                    limit={isFetched ? languageLimit : undefined}
                    error={!!error}
                    data-testid="identity-languages"
                  />

                  <FieldError>{error?.message}</FieldError>
                </>
              )}
            />
          </IdentityFieldRow>

          <MainPromptSheet
            open={openSheet === 'prompt'}
            onOpenChange={(o) => setOpenSheet(o ? 'prompt' : null)}
            control={kb.form.control}
            hasValue={kb.promptValue.length > 0}
            maxLength={mainPromptLimit}
            onApplyTemplate={(text) =>
              kb.form.setValue('short_description', text, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />

          {hasGuidelines && (
            <GuidelinesSheet
              open={openSheet === 'guidelines'}
              onOpenChange={(o) => setOpenSheet(o ? 'guidelines' : null)}
              control={kb.form.control}
              locked={aiGuidelinesLocked}
            />
          )}
        </Card>
      </TabsContent>
      <TabsContent value="model">
        <IdentityModelTab model={model} />
      </TabsContent>
    </Tabs>
  );
};
